// ... [All your imports stay the same]
import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Divider,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Box,
} from '@mui/material';
import axios from 'axios';

const initialInputs = {
  lcm: '',
  bcm: '',
  gsm: '',
  totalSheet: '',
  ratePerKg: '',
  printingChargePerThousand: '',
  plateCharge: '',
  cuttingChargePerThousand: '',
  conversionCost: '',
  packingDelivery: '',
  piecesPerSheet: '',
  salePricePerPiece: '',
  gstRate: '',
};

const getDefaultOutputs = () => ({
  wtPerSheet: 0,
  totalWeight: 0,
  totalSheetAmount: 0,
  totalPieces: 0,
  totalPrintingCost: 0,
  totalCuttingCharges: 0,
  miscellaneousCharges: 0,
  totalBasicCost: 0,
  pricePerPiece: 0,
  gstOnSale: 0,
  profitPerPiece: 0,
});

const BASE_URL = 'https://gsm-calculator-node.rupioo.com';

function App() {
  const [inputs, setInputs] = useState(initialInputs);
  const [outputs, setOutputs] = useState(getDefaultOutputs());
  const [savedList, setSavedList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editId, setEditId] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [itemName, setItemName] = useState('');

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    const res = await axios.get(`${BASE_URL}/api/calculation/get-all`);
    const dataWithExtras = res.data.map((entry) => ({
      ...entry,
      orderQuantity: entry.orderQuantity || '',
      orderRate: entry.orderRate || '',
    }));
    console.log(dataWithExtras);
    setSavedList(dataWithExtras);
  };

  useEffect(() => {
    const {
      lcm,
      bcm,
      gsm,
      totalSheet,
      ratePerKg,
      printingChargePerThousand,
      plateCharge,
      cuttingChargePerThousand,
      conversionCost,
      packingDelivery,
      piecesPerSheet,
      salePricePerPiece,
      gstRate,
    } = inputs;

    const wtPerSheet = lcm && bcm && gsm ? (lcm * bcm * gsm) / 10000000 : 0;
    const totalWeight = wtPerSheet * totalSheet;
    const totalSheetAmount = totalWeight * ratePerKg || 0;
    const totalPieces = totalSheet * piecesPerSheet;
    const totalPrintingCost =
      (totalSheet / 1000) * printingChargePerThousand + (plateCharge || 0);
    const totalCuttingCharges = cuttingChargePerThousand * (totalSheet / 10000);
    const miscellaneousCharges = (conversionCost || 0) + (packingDelivery || 0);
    const totalBasicCost =
      totalSheetAmount +
      totalPrintingCost +
      totalCuttingCharges +
      miscellaneousCharges;
    const pricePerPiece = totalPieces !== 0 ? totalBasicCost / totalPieces : 0;
    const gstOnSale = (salePricePerPiece * gstRate) / (100 + gstRate);
    const profitPerPiece = salePricePerPiece - (pricePerPiece + gstOnSale);

    setOutputs({
      wtPerSheet,
      totalWeight,
      totalSheetAmount,
      totalPieces,
      totalPrintingCost,
      totalCuttingCharges,
      miscellaneousCharges,
      totalBasicCost,
      pricePerPiece,
      gstOnSale,
      profitPerPiece,
    });
  }, [inputs]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: parseFloat(value) || '' }));
  };

  const handleSave = async () => {
    const payload = {
      customerName,
      itemName,
      inputs,
      outputs,
    };

    if (editId) {
      await axios.put(`${BASE_URL}/api/calculation/update/${editId}`, payload);
      setEditId(null);
    } else {
      await axios.post(`${BASE_URL}/api/calculation/save`, payload);
    }

    setInputs(initialInputs);
    setOutputs(getDefaultOutputs());
    setCustomerName('');
    setItemName('');
    fetchEntries();
  };

  const handleRowClick = (entry) => {
    setInputs(entry.inputs);
    setOutputs(entry.outputs);
    setCustomerName(entry.customerName);
    setItemName(entry.itemName);
    setEditId(entry._id);
  };

  const calculateTotalWeight = (lcm, bcm, gsm, quantity) => {
    return ((lcm * bcm * gsm) / 10000000) * quantity;
  };

  const calculateTotalAmount = (weight, rate) => {
    return weight * rate;
  };

  const handleCheckChange = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    await axios.post(`${BASE_URL}/api/calculation/bulk-delete`, {
      ids: selectedRows,
    });
    setSelectedRows([]);
    fetchEntries();
  };

  const handleOrderChange = async (entryId, field, value) => {
    const updatedList = savedList.map((entry) => {
      if (entry._id === entryId) {
        const updatedEntry = {
          ...entry,
          [field]: value,
        };
        const weight = calculateTotalWeight(
          entry.inputs.lcm,
          entry.inputs.bcm,
          entry.inputs.gsm,
          updatedEntry.orderQuantity || 0
        );
        const amount = calculateTotalAmount(
          weight,
          updatedEntry.orderRate || 0
        );
        updatedEntry.totalAmount = amount;

        // If orderQuantity changes, update totalSheet too
        if (field === 'orderQuantity') {
          updatedEntry.inputs.totalSheet = value;
        }

        if (field === 'orderRate') {
          updatedEntry.inputs.ratePerKg = value;
        }

        console.log(updatedEntry);

        // Save the changes to the backend
        axios.put(
          `${BASE_URL}/api/calculation/update/${entry._id}`,
          updatedEntry
        );

        return updatedEntry;
      }
      return entry;
    });

    setSavedList(updatedList);
  };

  const grandTotalAmount = savedList.reduce((sum, entry) => {
    const weight = calculateTotalWeight(
      entry.inputs.lcm,
      entry.inputs.bcm,
      entry.inputs.gsm,
      entry.orderQuantity || 0
    );
    const amount = calculateTotalAmount(weight, entry.orderRate || 0);
    return sum + amount;
  }, 0);

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom textAlign="center">
        GSM Calculator
      </Typography>

      <Typography variant="h6">Product Details</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Product Name"
            fullWidth
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Product Size"
            fullWidth
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
          />
        </Grid>
      </Grid>

      <Typography variant="h6">Input Fields</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.keys(initialInputs).map((key) => (
          <Grid item xs={12} sm={6} md={4} key={key}>
            <TextField
              label={key
                .replace(/([A-Z])/g, ' $1')
                .replace(/^\w/, (c) => c.toUpperCase())}
              name={key}
              type="number"
              fullWidth
              value={inputs[key]}
              onChange={handleInputChange}
              variant="outlined"
            />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="h6">Calculated Outputs</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.entries(outputs).map(([key, value]) => (
          <Grid item xs={12} sm={6} md={4} key={key}>
            <TextField
              label={key
                .replace(/([A-Z])/g, ' $1')
                .replace(/^\w/, (c) => c.toUpperCase())}
              value={typeof value === 'number' ? value.toFixed(2) : ''}
              fullWidth
              variant="filled"
              InputProps={{ readOnly: true }}
            />
          </Grid>
        ))}
      </Grid>

      <Button variant="contained" onClick={handleSave} color="primary">
        {editId ? 'Update' : 'Save'}
      </Button>

      {selectedRows.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button color="error" variant="contained" onClick={handleBulkDelete}>
            Delete Selected
          </Button>
        </Box>
      )}

      <Typography variant="h6" sx={{ mt: 5 }}>
        Saved Entries
      </Typography>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"></TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>LCM</TableCell>
              <TableCell>BCM</TableCell>
              <TableCell>GSM</TableCell>
              <TableCell>Order Quantity</TableCell>
              <TableCell>Total Weight</TableCell>
              <TableCell>Order Rate</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Price per Piece</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {savedList.map((entry) => {
              const totalWeight = calculateTotalWeight(
                entry.inputs.lcm,
                entry.inputs.bcm,
                entry.inputs.gsm,
                entry.orderQuantity || 0
              );
              const totalAmount = calculateTotalAmount(
                totalWeight,
                entry.orderRate || 0
              );
              const pricePerPiece = entry.outputs?.pricePerPiece || 0;

              return (
                <TableRow key={entry._id} onClick={() => handleRowClick(entry)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRows.includes(entry._id)}
                      onChange={() => handleCheckChange(entry._id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell>{entry.customerName}</TableCell>
                  <TableCell>{entry.itemName}</TableCell>
                  <TableCell>{entry.inputs.lcm}</TableCell>
                  <TableCell>{entry.inputs.bcm}</TableCell>
                  <TableCell>{entry.inputs.gsm}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={entry.orderQuantity}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        handleOrderChange(
                          entry._id,
                          'orderQuantity',
                          e.target.value
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>{totalWeight.toFixed(2)}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={entry.orderRate}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        handleOrderChange(
                          entry._id,
                          'orderRate',
                          e.target.value
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>{totalAmount.toFixed(2)}</TableCell>
                  <TableCell>{pricePerPiece.toFixed(2)}</TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell colSpan={9} align="right">
                <strong>Grand Total Amount:</strong>
              </TableCell>
              <TableCell>
                <strong>{grandTotalAmount.toFixed(2)}</strong>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}

export default App;
